/**
 * THE GEO ENGINE — aggregator and gate.
 *
 * One module is the source of truth for which locations exist. The React app,
 * the prerenderer, the sitemap generator and the llms.txt generator all read
 * from here, so they cannot disagree about the site's shape.
 *
 * Data is split per corridor exactly as the source engine split it, so each
 * file stays reviewable and a visitor's bundle carries only what its page
 * imports.
 */
import type { Location, FlightStatus } from "./types";
import { REGIONS, getRegion } from "./regions";
import { CORE_LOCATIONS } from "./core";
import { ACREAGE_LOCATIONS } from "./acreage";
import { RESTRICTED_LOCATIONS } from "./restricted";
import { CORRIDOR_LOCATIONS } from "./corridor";
import { ROSTER_LOCATIONS } from "./roster";

export type {
  Location,
  Region,
  AirspaceFact,
  AirspaceRecord,
  AirspaceSource,
  FlightStatus,
  LocationFAQ,
  LocationTier,
  SubjectMatter,
} from "./types";
export { REGIONS, getRegion };

export const ALL_LOCATIONS: Location[] = [
  ...CORE_LOCATIONS,
  ...ACREAGE_LOCATIONS,
  ...RESTRICTED_LOCATIONS,
  ...CORRIDOR_LOCATIONS,
  ...ROSTER_LOCATIONS,
];

export function getLocation(slug: string): Location | undefined {
  return ALL_LOCATIONS.find((l) => l.slug === slug);
}

export function locationsByRegion(regionSlug: string): Location[] {
  return ALL_LOCATIONS.filter((l) => l.region === regionSlug).sort(
    (a, b) => a.tier - b.tier || a.name.localeCompare(b.name),
  );
}

/** Regions that actually have at least one location. Empty hubs are not routes. */
export function populatedRegions() {
  return REGIONS.filter((r) => locationsByRegion(r.slug).length > 0);
}

/** Locations where flight is restricted — these render the §8.5 archetype. */
export function isRestricted(l: Location): boolean {
  return l.flightStatus === "permit-required" || l.flightStatus === "prohibited";
}

/* ─────────────────────────────────────────────────────────────────────────────
   THE 5-OF-10 UNIQUENESS GATE  —  Operating Brief §7.2, Iron Law 3.

   v1 of the brief said "run the gate at build time" and specified no mechanism,
   which made it unrunnable and therefore decorative. This is the mechanism.

   Signal 1 (airspace reality) is MANDATORY and is satisfied only at ladder
   levels 1 and 2 — a site-confirmed record, or a corridor-level record that the
   copy narrows honestly. `regional-inference` counts, because the corridor claim
   is real and the copy states that the specific site is confirmed before every
   flight. What does not count is an empty or speculative record.

   A failing entry does not throw. It renders (so the URL works without JS) but
   is excluded from the sitemap and carries noindex, with its reason logged.
   ─────────────────────────────────────────────────────────────────────────── */

export interface GateResult {
  slug: string;
  signals: string[];
  score: number;
  hasMandatory: boolean;
  passes: boolean;
  reasons: string[];
}

export function evaluateGate(l: Location): GateResult {
  const signals: string[] = [];
  const reasons: string[] = [];

  // Signal 1 — airspace reality (MANDATORY)
  const hasMandatory =
    Boolean(l.airspace?.classification) &&
    Boolean(l.airspace?.nearestAerodrome) &&
    Boolean(l.airspace?.authorizationNote) &&
    Boolean(l.airspace?.source) &&
    Boolean(l.airspace?.lastVerified);
  if (hasMandatory) signals.push("airspace");
  else reasons.push("signal 1 (airspace reality) incomplete — page cannot be indexed");

  // Signal 2 — named landmarks
  if (l.landmarks?.length >= 3) signals.push("landmarks");
  else reasons.push("fewer than 3 named landmarks");

  // Signal 3 — terrain
  if (l.terrain && l.terrain.length >= 60) signals.push("terrain");

  // Signal 4 — weather / light window
  if (l.windNote && l.seasonalWindow) signals.push("weather-light");

  // Signal 5 — municipal / park restriction
  if (l.bylawNote && l.bylawNote.length >= 40) signals.push("bylaw");

  // Signal 6 — subject-matter mix
  if (l.subjectMix?.length >= 2) signals.push("subject-mix");
  else reasons.push("fewer than 2 subject-matter types");

  // Signal 7 — mobilisation reality
  if (l.mobilisationNote && typeof l.driveMinutesFromCalgary === "number")
    signals.push("mobilisation");

  // Signal 8 — real project reference (consented only)
  if (l.realProjects?.some((p) => p.consent)) signals.push("real-project");

  // Signal 9 — real testimonial (consented only)
  if (l.realTestimonial?.consent) signals.push("real-testimonial");

  // Signal 10 — location-specific FAQ
  if (l.faqs?.length >= 2) signals.push("local-faq");
  else reasons.push("fewer than 2 location-specific FAQs");

  // Structural integrity, independent of the signal count
  if (!l.airspaceFact?.hook || !l.airspaceFact?.consequence || !l.airspaceFact?.resolution)
    reasons.push("airspaceFact missing one or more of its three beats");
  if (!l.primaryKeywords?.length) reasons.push("no primary keyword");
  if (!l.nearestLocations?.length) reasons.push("no sideways mesh — page would be an orphan");

  const score = signals.length;
  const structurallySound =
    Boolean(l.airspaceFact?.hook && l.airspaceFact?.consequence && l.airspaceFact?.resolution) &&
    Boolean(l.primaryKeywords?.length) &&
    Boolean(l.nearestLocations?.length);

  const passes = hasMandatory && score >= 5 && structurallySound;
  if (!passes && score < 5) reasons.push(`only ${score} of 10 signals present (5 required)`);

  return { slug: l.slug, signals, score, hasMandatory, passes, reasons };
}

/** Effective indexability: the explicit flag AND the gate must both allow it. */
export function isIndexable(l: Location): boolean {
  if (l.indexable === false) return false;
  return evaluateGate(l).passes;
}

/** What the sitemap and the region grids show. */
export function indexableLocations(): Location[] {
  return ALL_LOCATIONS.filter(isIndexable);
}

/** Every entry that failed, with its reason. Printed by the build. */
export function gateFailures(): GateResult[] {
  return ALL_LOCATIONS.map(evaluateGate).filter((r) => !r.passes);
}

/* ─────────────────────────────────────────────────────────────────────────────
   INTEGRITY CHECKS — run by the test suite and by the build.
   These catch the failure modes that are invisible in a single-page review.
   ─────────────────────────────────────────────────────────────────────────── */

export interface IntegrityIssue {
  kind:
    | "duplicate-slug"
    | "duplicate-primary-keyword"
    | "broken-nearest"
    | "unknown-region"
    | "shared-hook"
    | "stale-airspace"
    | "restricted-sold-as-routine";
  detail: string;
}

export function checkIntegrity(now = new Date()): IntegrityIssue[] {
  const issues: IntegrityIssue[] = [];
  const slugs = new Set<string>();
  const keywords = new Map<string, string>();
  const regionSlugs = new Set(REGIONS.map((r) => r.slug));

  for (const l of ALL_LOCATIONS) {
    if (slugs.has(l.slug))
      issues.push({ kind: "duplicate-slug", detail: l.slug });
    slugs.add(l.slug);

    // Iron Law 10 — one keyword, one URL.
    const primary = l.primaryKeywords?.[0]?.toLowerCase();
    if (primary) {
      const owner = keywords.get(primary);
      if (owner)
        issues.push({
          kind: "duplicate-primary-keyword",
          detail: `"${primary}" claimed by both ${owner} and ${l.slug}`,
        });
      else keywords.set(primary, l.slug);
    }

    if (!regionSlugs.has(l.region))
      issues.push({ kind: "unknown-region", detail: `${l.slug} → ${l.region}` });

    // Airspace facts age. The provenance audit enforces 12 months.
    const verified = new Date(l.airspace.lastVerified);
    const monthsOld =
      (now.getTime() - verified.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
    if (Number.isNaN(monthsOld) || monthsOld > 12)
      issues.push({
        kind: "stale-airspace",
        detail: `${l.slug} last verified ${l.airspace.lastVerified}`,
      });

    // A restricted location must never render the standard sales archetype.
    const restrictedStatuses: FlightStatus[] = ["permit-required", "prohibited"];
    if (restrictedStatuses.includes(l.flightStatus) && !l.airspace.restrictedZones?.length)
      issues.push({
        kind: "restricted-sold-as-routine",
        detail: `${l.slug} is ${l.flightStatus} but names no restricted zone`,
      });
  }

  // Sideways mesh must resolve, or the page is an orphan.
  for (const l of ALL_LOCATIONS) {
    for (const near of l.nearestLocations) {
      if (!slugs.has(near))
        issues.push({
          kind: "broken-nearest",
          detail: `${l.slug} → ${near} (does not exist)`,
        });
    }
  }

  // No two neighbouring locations may share an airspace hook — this is what
  // makes the copy un-templatable. Checked only between actual neighbours,
  // because two unrelated towns coincidentally sharing a phrase is harmless.
  for (const l of ALL_LOCATIONS) {
    for (const near of l.nearestLocations) {
      const other = getLocation(near);
      if (other && other.airspaceFact.hook === l.airspaceFact.hook)
        issues.push({
          kind: "shared-hook",
          detail: `${l.slug} and ${near} share an airspace hook`,
        });
    }
  }

  return issues;
}

/** Unique municipalities across the roster. */
export function territoryCities(): string[] {
  return [...new Set(ALL_LOCATIONS.map((l) => l.city))].sort();
}

/**
 * THE SIDEWAYS MESH, RESOLVED — never renders a broken link.
 *
 * `nearestLocations` is authored by hand, and the roster is published in waves
 * (Operating Brief §15.3), so a wave-2 entry can legitimately reference a
 * wave-4 neighbour that does not exist yet. `checkIntegrity` reports those as
 * work outstanding, which is correct. This function makes sure the *rendered*
 * page never carries a 404: unresolved slugs are dropped and the list is
 * backfilled from the same region, then from the nearest by great-circle
 * distance, so every page keeps a full mesh and no page is ever an orphan.
 */
export function resolveNearest(l: Location, limit = 5): Location[] {
  const out: Location[] = [];
  const seen = new Set<string>([l.slug]);

  for (const slug of l.nearestLocations) {
    const found = getLocation(slug);
    if (found && isIndexable(found) && !seen.has(found.slug)) {
      out.push(found);
      seen.add(found.slug);
    }
  }

  if (out.length < limit) {
    for (const sibling of locationsByRegion(l.region)) {
      if (out.length >= limit) break;
      if (!seen.has(sibling.slug) && isIndexable(sibling)) {
        out.push(sibling);
        seen.add(sibling.slug);
      }
    }
  }

  if (out.length < limit) {
    const byDistance = ALL_LOCATIONS.filter(
      (o) => !seen.has(o.slug) && isIndexable(o),
    ).sort((a, b) => haversineKm(l, a) - haversineKm(l, b));
    for (const near of byDistance) {
      if (out.length >= limit) break;
      out.push(near);
      seen.add(near.slug);
    }
  }

  return out.slice(0, limit);
}

/** Great-circle distance in km — powers the nearby module and the SVG map. */
export function haversineKm(a: Location, b: Location): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.coordinates.lat - a.coordinates.lat);
  const dLng = toRad(b.coordinates.lng - a.coordinates.lng);
  const lat1 = toRad(a.coordinates.lat);
  const lat2 = toRad(b.coordinates.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
